// /src/views/DeliveryApp/DeliveryAppSettingsList.js

import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../../static/css/App.css";
import "../../static/css/customerTable.css";
import "bootstrap/dist/css/bootstrap.min.css";

import { Container, Button, Modal, Form, Row, Col } from "react-bootstrap";
import { Box } from "@mui/material";
import { DataGrid, GridToolbar, gridClasses } from "@mui/x-data-grid";
import moment from "moment";
import { showConfirm } from "react-confirm-prompt";

import DynamicBreadcrumb from "../header/breadcrumb";
import Loader from "../loader/loader";
import SearchableDropdown from "../components/SearchableDropdown";

import branchApi from "../../services/branchapi";
import deliveryAppSettingsApi from "../../services/deliveryAppSettingsApi"; // ✅ create this service as discussed

const DeliveryAppSettingsList = () => {
    // table
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);

    const [refreshTable, setRefreshTable] = useState(false);
    const [sortModel, setSortModel] = useState([]);
    const [filterModel, setFilterModel] = useState({ items: [], quickFilterValues: [] });
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 15 });

    // filters (top)
    const [scope, setScope] = useState("ALL"); // ALL | GLOBAL | BRANCH
    const [status, setStatus] = useState("ALL"); // ALL | 1 | 0

    const [branchId, setBranchId] = useState(0);
    const [branchName, setBranchName] = useState("All");
    const [allBranches, setAllBranches] = useState([]);

    // modal add/edit
    const [showModal, setShowModal] = useState(false);
    const [editingRow, setEditingRow] = useState(null);

    const [form, setForm] = useState({
        scope: "GLOBAL",
        branch_id: null,
        setting_key: "",
        data_type: "STRING",
        setting_value: "",
        status: 1,
    });

    const [errors, setErrors] = useState({});
    const [loaderVisible, setLoaderVisible] = useState(false);

    const pathArray = [{ name: "Delivery App Settings", url: null }];

    // ---------------------------
    // Helpers
    // ---------------------------
    const resetForm = () => {
        setForm({
            scope: "GLOBAL",
            branch_id: null,
            setting_key: "",
            data_type: "STRING",
            setting_value: "",
            status: 1,
        });
        setErrors({});
    };

    const openAddModal = () => {
        setEditingRow(null);
        resetForm();
        setShowModal(true);
    };

    const openEditModal = (row) => {
        setEditingRow(row);
        setForm({
            scope: row.scope || "GLOBAL",
            branch_id: row.branch_id || null,
            setting_key: row.setting_key || "",
            data_type: row.data_type || "STRING",
            setting_value: row.setting_value ?? "",
            status: Number(row.status ?? 1),
        });
        setErrors({});
        setShowModal(true);
    };

    const validateForm = () => {
        const e = {};
        if (!form.scope) e.scope = "Scope is required";
        if (!form.setting_key || !form.setting_key.trim()) e.setting_key = "Setting Key is required";
        if (!form.data_type) e.data_type = "Data Type is required";
        if (form.setting_value === null || form.setting_value === undefined || String(form.setting_value).trim() === "")
            e.setting_value = "Setting Value is required";

        if (form.scope === "BRANCH") {
            if (!form.branch_id || Number(form.branch_id) <= 0) e.branch_id = "Branch is required for BRANCH scope";
        }
        return e;
    };

    const deriveSearch = () => {
        // Same pattern used across UI: quickFilterValues[0] used as search
        const q = filterModel?.quickFilterValues?.[0];
        return q ? String(q) : null;
    };

    const mapScopeToApi = () => {
        if (scope === "ALL") return null;
        return scope; // GLOBAL/BRANCH
    };

    const mapStatusToApi = () => {
        if (status === "ALL") return null;
        return status; // "1" or "0"
    };

    // ---------------------------
    // Fetch branches for dropdown
    // ---------------------------
    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const resp = await branchApi.getAllBranches({ status: 1, limit: 500, page: 0 });
                const list = (resp?.data?.result || []).sort((a, b) =>
                    (a.branchName || "").localeCompare(b.branchName || "")
                );
                setAllBranches(list);
            } catch (err) {
                console.error("Failed to fetch branches:", err);
            }
        };
        fetchBranches();
    }, []);

    // ---------------------------
    // Fetch table data
    // ---------------------------
    const fetchSettings = useCallback(async () => {
        setLoading(true);
        try {
            const { page, pageSize } = paginationModel;

            const resp = await deliveryAppSettingsApi.list({
                limit: pageSize,
                page,
                search: deriveSearch(),
                scope: mapScopeToApi(),
                status: mapStatusToApi(),
                branch_id: branchId && Number(branchId) > 0 ? Number(branchId) : null,
                sortModel,
                filterModel,
            });

            // Expecting Common.buildResponse style:
            // { status:"SUCCESS", data: { result: [...], totalRecords: [{total_records: N}] } }
            const result = resp?.data?.result || [];
            const total = resp?.data?.totalRecords?.[0]?.total_records || 0;

            const formatted = result.map((r) => ({
                id: r.id,
                scope: r.scope,
                branch_id: r.branch_id,
                branch_name: r.branch_id
                    ? (allBranches.find((b) => Number(b.branchId) === Number(r.branch_id))?.branchName || `Branch #${r.branch_id}`)
                    : "GLOBAL",
                setting_key: r.setting_key,
                data_type: r.data_type,
                setting_value: r.setting_value,
                status: r.status,
                created_at: r.created_at ? moment(r.created_at).format("YYYY/MM/DD kk:mm:ss") : "",
                updated_at: r.updated_at ? moment(r.updated_at).format("YYYY/MM/DD kk:mm:ss") : "",
            }));

            setRows(formatted);
            setTotalRecords(total);
        } catch (err) {
            console.error("Error fetching settings:", err);
            setRows([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    }, [paginationModel, sortModel, filterModel, refreshTable, scope, status, branchId, allBranches]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    // ---------------------------
    // Save (Create / Update)
    // ---------------------------
    const handleSave = async (e) => {
        e.preventDefault();

        const eMap = validateForm();
        if (Object.keys(eMap).length > 0) {
            setErrors(eMap);
            return;
        }
        setErrors({});

        try {
            setLoaderVisible(true);

            const payload = {
                scope: form.scope,
                branch_id: form.scope === "BRANCH" ? Number(form.branch_id) : null,
                setting_key: form.setting_key.trim(),
                data_type: form.data_type,
                setting_value: String(form.setting_value),
                status: Number(form.status),
            };

            let resp;
            if (editingRow?.id) {
                resp = await deliveryAppSettingsApi.update(editingRow.id, payload);
            } else {
                resp = await deliveryAppSettingsApi.create(payload);
            }

            if (resp?.status === "SUCCESS" || resp?.success === true) {
                setShowModal(false);
                setTimeout(() => {
                    showConfirm("Success", {
                        description: resp?.message || "Saved successfully",
                        type: "Success",
                        hideCancel: true,
                    }).then(() => {
                        setRefreshTable((p) => !p);
                    });
                }, 200);
            } else {
                setShowModal(false);
                setTimeout(() => {
                    showConfirm("Oops!", {
                        description: resp?.message || "Process failed, try again.",
                        type: "warning",
                        hideCancel: true,
                    });
                }, 200);
            }
        } catch (err) {
            // console.error(err);
            showConfirm("Oops!", {
                description: err?.message || "Process failed, try again.",
                type: "warning",
                hideCancel: true,
            });
        } finally {
            setLoaderVisible(false);
        }
    };

    // ---------------------------
    // Toggle status
    // ---------------------------
    const handleToggleStatus = async (row) => {
        const isActive = Number(row.status) === 1;
        const nextStatus = isActive ? 0 : 1;

        const sureTxt = isActive ? "Change status to inactive?" : "Change status to active?";
        const decTxt = isActive ? "This will inactivate the setting" : "This will activate the setting";

        showConfirm(sureTxt, { description: decTxt, type: "info" }).then(async (answer) => {
            if (!answer) return;

            try {
                setLoaderVisible(true);
                const resp = await deliveryAppSettingsApi.updateStatus(row.id, nextStatus);

                if (resp?.status === "SUCCESS" || resp?.success === true) {
                    showConfirm("Success", {
                        description: resp?.message || "Status updated",
                        type: "Success",
                        hideCancel: true,
                    }).then(() => setRefreshTable((p) => !p));
                } else {
                    showConfirm("Oops!", {
                        description: resp?.message || "Unable to update status",
                        type: "warning",
                        hideCancel: true,
                    });
                }
            } catch (err) {
                console.error(err);
                showConfirm("Oops!", {
                    description: err?.message || "Unable to update status",
                    type: "warning",
                    hideCancel: true,
                });
            } finally {
                setLoaderVisible(false);
            }
        });
    };

    // ---------------------------
    // Columns
    // ---------------------------
    const columns = useMemo(
        () => [
            { field: "id", headerName: "ID", width: 80 },
            { field: "scope", headerName: "Scope", width: 110 },
            { field: "branch_name", headerName: "Branch", width: 200 },
            { field: "setting_key", headerName: "Key", width: 260 },
            { field: "data_type", headerName: "Type", width: 110 },
            {
                field: "setting_value",
                headerName: "Value",
                flex: 1,
                minWidth: 220,
                renderCell: (params) => {
                    const v = params?.value ?? "";
                    const s = String(v);
                    return s.length > 60 ? `${s.slice(0, 60)}...` : s;
                },
            },
            { field: "updated_at", headerName: "Updated At", width: 190 },
            {
                field: "status",
                headerName: "Status",
                width: 110,
                renderCell: (params) => (Number(params.value) === 1 ? "Active" : "Inactive"),
            },
            {
                field: "actions",
                headerName: "Actions",
                width: 220,
                sortable: false,
                filterable: false,
                renderCell: (params) => {
                    const row = params.row;
                    const isActive = Number(row.status) === 1;
                    return (
                        <div style={{ display: "flex", gap: "8px" }}>
                            <Button size="sm" variant="primary" onClick={() => openEditModal(row)}>
                                Edit
                            </Button>
                            <Button
                                size="sm"
                                variant={isActive ? "warning" : "success"}
                                onClick={() => handleToggleStatus(row)}
                            >
                                {isActive ? "Inactivate" : "Activate"}
                            </Button>
                        </div>
                    );
                },
            },
        ],
        [allBranches]
    );

    // ---------------------------
    // Top filter UI
    // ---------------------------
    const branchOptions = useMemo(() => {
        const base = [{ name: "All", value: 0 }];
        const mapped = allBranches.map((b) => ({
            name: b.branchName ? String(b.branchName) : `Branch #${b.branchId}`,
            value: b.branchId,
        }));
        return base.concat(mapped);
    }, [allBranches]);

    return (
        <Container fluid>
            <DynamicBreadcrumb path={pathArray} />

            <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 style={{ margin: 0 }}>Delivery App Settings</h5>
                <Button variant="success" onClick={openAddModal}>
                    + Add Setting
                </Button>
            </div>

            {/* Filters */}
            <div className="card p-2 mb-2">
                <Row className="g-2">
                    <Col md={3}>
                        <Form.Label className="mb-1">Scope</Form.Label>
                        <Form.Select value={scope} onChange={(e) => setScope(e.target.value)}>
                            <option value="ALL">All</option>
                            <option value="GLOBAL">GLOBAL</option>
                            <option value="BRANCH">BRANCH</option>
                        </Form.Select>
                    </Col>

                    <Col md={3}>
                        <Form.Label className="mb-1">Status</Form.Label>
                        <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                            <option value="ALL">All</option>
                            <option value="1">Active</option>
                            <option value="0">Inactive</option>
                        </Form.Select>
                    </Col>

                    <Col md={6}>
                        <Form.Label className="mb-1">Branch (optional)</Form.Label>
                        <SearchableDropdown
                            options={allBranches}
                            extraoption={{ branchId: 0, branchName: "All" }}
                            label="branchName"
                            id="branchId"
                            selectedVal={branchName}
                            handleChange={(v) => {
                                // v = { id: option[branchId], name: option[branchName] }
                                setBranchId(v?.id ?? 0);
                                setBranchName(v?.name ?? "All");
                            }}
                        />
                    </Col>
                </Row>

                <div className="mt-2" style={{ fontSize: 12, opacity: 0.8 }}>
                    Tip: Use the table search box (top right) to search by key/value.
                </div>
            </div>

            {/* Table */}
            <Box
                sx={{
                    height: 650,
                    width: "100%",
                    "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "bold" },
                    [`& .${gridClasses.row}`]: { borderBottom: "1px solid #f0f0f0" },
                }}
            >
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    disableRowSelectionOnClick
                    sortingMode="server"
                    filterMode="server"
                    paginationMode="server"
                    rowCount={totalRecords}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                    filterModel={filterModel}
                    onFilterModelChange={setFilterModel}
                    pageSizeOptions={[10, 15, 25, 50]}
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 500 } },
                    }}
                />
            </Box>

            {/* Add/Edit Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{editingRow ? "Edit Setting" : "Add Setting"}</Modal.Title>
                </Modal.Header>

                <Form onSubmit={handleSave}>
                    <Modal.Body>
                        <Row className="g-3">
                            <Col md={4}>
                                <Form.Label>Scope</Form.Label>
                                <Form.Select
                                    value={form.scope}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setForm((p) => ({
                                            ...p,
                                            scope: v,
                                            branch_id: v === "BRANCH" ? p.branch_id : null,
                                        }));
                                    }}
                                    isInvalid={!!errors.scope}
                                >
                                    <option value="GLOBAL">GLOBAL</option>
                                    <option value="BRANCH">BRANCH</option>
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">{errors.scope}</Form.Control.Feedback>
                            </Col>

                            <Col md={8}>
                                <Form.Label>Branch (only for BRANCH scope)</Form.Label>
                                {form.scope === "BRANCH" ? (
                                    <SearchableDropdown
                                        options={allBranches}
                                        label="branchName"
                                        id="branchId"
                                        selectedVal={
                                            form.branch_id
                                                ? (allBranches.find((b) => Number(b.branchId) === Number(form.branch_id))?.branchName || "")
                                                : ""
                                        }
                                        handleChange={(v) => {
                                            setForm((p) => ({ ...p, branch_id: v?.id || null }));
                                        }}
                                    />
                                ) : (
                                    <Form.Control value="(Not required for GLOBAL)" disabled />
                                )}
                                {errors.branch_id && (
                                    <div style={{ color: "#dc3545", fontSize: 12, marginTop: 4 }}>
                                        {errors.branch_id}
                                    </div>
                                )}

                            </Col>

                            <Col md={6}>
                                <Form.Label>Setting Key</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={form.setting_key}
                                    onChange={(e) => setForm((p) => ({ ...p, setting_key: e.target.value }))}
                                    isInvalid={!!errors.setting_key}
                                    placeholder="e.g. MIN_CONFIRM_DISTANCE_M"
                                />
                                <Form.Control.Feedback type="invalid">{errors.setting_key}</Form.Control.Feedback>
                            </Col>

                            <Col md={3}>
                                <Form.Label>Data Type</Form.Label>
                                <Form.Select
                                    value={form.data_type}
                                    onChange={(e) => setForm((p) => ({ ...p, data_type: e.target.value }))}
                                    isInvalid={!!errors.data_type}
                                >
                                    <option value="STRING">STRING</option>
                                    <option value="NUMBER">NUMBER</option>
                                    <option value="BOOLEAN">BOOLEAN</option>
                                    <option value="JSON">JSON</option>
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">{errors.data_type}</Form.Control.Feedback>
                            </Col>

                            <Col md={3}>
                                <Form.Label>Status</Form.Label>
                                <Form.Select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: Number(e.target.value) }))}>
                                    <option value={1}>Active</option>
                                    <option value={0}>Inactive</option>
                                </Form.Select>
                            </Col>

                            <Col md={12}>
                                <Form.Label>Setting Value</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    value={form.setting_value}
                                    onChange={(e) => setForm((p) => ({ ...p, setting_value: e.target.value }))}
                                    isInvalid={!!errors.setting_value}
                                    placeholder={
                                        form.data_type === "JSON"
                                            ? `e.g. {"meters":80}`
                                            : form.data_type === "BOOLEAN"
                                                ? "true / false"
                                                : "value"
                                    }
                                />
                                <Form.Control.Feedback type="invalid">{errors.setting_value}</Form.Control.Feedback>
                            </Col>

                            <Col md={12} style={{ fontSize: 12, opacity: 0.85 }}>
                                For <b>BOOLEAN</b>, use <code>true</code> or <code>false</code>. For <b>JSON</b>, paste valid JSON.
                            </Col>
                        </Row>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit">
                            {editingRow ? "Update" : "Create"}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Loader overlay */}
            {loaderVisible ? <Loader /> : null}
        </Container>
    );
};

export default DeliveryAppSettingsList;